/**
 * `useNotifications()` — the single source for the header bell and the
 * `/notifications` page.
 *
 * Notifications are **derived** from the platform's own truth through existing
 * reads — recent pipeline runs, and everything waiting on you
 * (`GET /api/v1/me/waiting`, ADR-0070: release validation gates, signatures,
 * reviews and QA acceptances; tests assigned to you; pipeline approvals you may
 * decide; for Administrators, support tickets and Veeva approvals). There is no
 * notifications table and no server-side state: nothing here is a controlled
 * record, and an event that matters for compliance is already an `audit_event`
 * written when it happened.
 *
 * The two families behave differently on purpose. Waiting items are **to-dos**
 * — they exist while the work is outstanding and leave the list once it is done
 * — so they sort first. Pipeline items are a **log** (below).
 *
 * **Every run that is news gets its own notification** — this is a log, not a
 * status board. A pipeline that failed three times overnight shows three rows,
 * because "it failed at 02:00, 03:00 and 04:00" is a different fact from "it is
 * failing", and the second one loses the history. A manual run that succeeded is
 * still the one thing that produces nothing: you were watching it finish.
 *
 * On top of that sit two **per-viewer states**, both sets of notification ids in
 * `localStorage`, keyed by user id:
 *
 * - **read** — acknowledged, so the badge can be cleared while the runs stay
 *   exactly as they are.
 * - **dismissed** — deleted from this person's view. The notification is derived
 *   from a run that still exists, so without remembering the dismissal it would
 *   simply come back on the next poll.
 *
 * Both are conveniences, never controlled records — nothing about what someone
 * glanced at or tidied away belongs in the audit trail, and the events that
 * matter were written as `audit_event` rows when the runs happened. So they live
 * in the browser, are wrapped in try/catch, and degrade to "nothing read,
 * nothing dismissed" if storage is unavailable.
 *
 * The feed reaches back **seven days** (the API's window). Ids of runs that have
 * aged out are pruned from both sets, so neither can grow without bound.
 *
 * The waiting items come from the one shared poll (`waitingStore.ts`, ADO
 * 2546) that Home reads too; this hook polls the pipeline log itself.
 * Both sources are every role's: the server decides what is waiting on whom,
 * from the person's own agents, role and place on each run — so a plain User
 * named approver on a run is told when it is their turn. A poll where any
 * source fails keeps the last state and retries next tick.
 *
 * **Polls are background requests** (`X-Daquma-Background`, ADR-0046): a bell
 * ticking in an idle tab is not a person, so it does not keep the session
 * alive.
 *
 * **The polling effect depends on nothing that changes — deliberately.**
 * `useAccessToken()` is a `useCallback` over `[instance, accounts,
 * config.scopes]`, and msal-react hands back fresh `accounts`/`scopes` arrays on
 * most renders, so `getToken` is a *new function* nearly every render. An effect
 * that lists it **and** sets state in its body re-runs forever: effect →
 * setState → render → new `getToken` → effect → … That is the settings-page
 * flood in `docs/bugs/SOLVED.md`, and here it would point at the run feed. Live
 * values are read through refs, and the state only changes when the
 * notifications actually changed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMe } from "@/features/users/useMe";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { type RecentRunOut, listRecentRuns } from "@/shared/api/projects";
import { waitingNotifications } from "./waitingNotifications";
import { useWaitingItems } from "./waitingStore";

const POLL_MS = 60_000;

/** How the run was launched, in words a notification can use. */
const TRIGGER_LABEL: Record<string, string> = {
  manual: "Manual",
  schedule: "Scheduled",
  blob: "File-triggered",
  validation_test_data: "Validation run (test data)",
};

/** A failure's own message can be long; the row shows the beginning of it and
 *  the pipeline page has the rest. */
const MAX_DETAIL = 160;

/** `failed` / `finished` are the pipeline log; the rest are things waiting on
 *  you (`waitingNotifications`): a run's gate, a signature, a review, a test,
 *  an approval, a support ticket. */
export type NotificationKind =
  | "failed"
  | "finished"
  | "gate"
  | "sign"
  | "review"
  | "test"
  | "approval"
  | "ticket";

/** The kinds that are to-dos rather than the pipeline log. */
export const WAITING_KINDS: readonly NotificationKind[] = [
  "gate",
  "sign",
  "review",
  "test",
  "approval",
  "ticket",
];

export interface AppNotification {
  /** Stable identity. A pipeline notification is keyed on the run id, so a later
   *  run of the same pipeline is a different notification; a validation one
   *  changes when the actionable state changes (next gate, new assignment). */
  id: string;
  kind: NotificationKind;
  /** The pipeline or validation run this concerns — the primary line. */
  title: string;
  /** What happened, or what is needed — the secondary line. */
  detail: string;
  /** The underlying status, for the row's icon and colour. */
  status: string;
  /** When it happened (ISO-8601, UTC). A log needs timestamps — "it failed"
   *  and "it failed at 02:14" are different amounts of help. */
  at: string | null;
  /** Where clicking the row takes the user. */
  to: string;
}

/** The two per-viewer id sets. Same shape, same lifecycle, different meaning. */
type IdSet = "read" | "dismissed";

function storageKey(kind: IdSet, userId: string | undefined): string | null {
  return userId ? `daquma:notif-${kind}:${userId}` : null;
}

function loadIds(kind: IdSet, userId: string | undefined): Set<string> {
  const key = storageKey(kind, userId);
  if (!key) return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? (parsed as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveIds(kind: IdSet, userId: string | undefined, ids: Set<string>): void {
  const key = storageKey(kind, userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // Private window, storage disabled — the bell still works, it just cannot
    // remember what was read or deleted across reloads.
  }
}

function trigger(run: RecentRunOut): string {
  return TRIGGER_LABEL[run.trigger] ?? "Pipeline";
}

function truncate(text: string): string {
  const clean = text.trim().replace(/\s+/g, " ");
  return clean.length > MAX_DETAIL ? `${clean.slice(0, MAX_DETAIL - 1)}…` : clean;
}

/**
 * Turn one run into the notification it warrants, or `null` when it warrants
 * none. Exported for the unit tests — the interesting rules live here.
 */
export function notificationFor(run: RecentRunOut): AppNotification | null {
  if (!run.project_id) return null; // cannot be linked to; the API drops these too
  const to = `/projects/${run.project_id}/pipelines/${run.pipeline_id}`;
  const title = run.pipeline_name ?? "Pipeline";

  // A run paused at an Approval node is not logged here: the approval comes
  // from the waiting summary, to the people who may decide it (ADR-0070).
  if (run.status === "failed") {
    return {
      id: `run:${run.run_id}`,
      kind: "failed",
      title,
      detail: run.error
        ? `${trigger(run)} run failed, ${truncate(run.error)}`
        : `${trigger(run)} run failed.`,
      status: run.status,
      at: run.ended_at_utc,
      to,
    };
  }
  // A manual run that worked is not news — the person who started it watched it
  // finish. An unattended one is the only success worth a notification.
  if (run.status === "succeeded" && run.trigger !== "manual") {
    return {
      id: `run:${run.run_id}`,
      kind: "finished",
      title,
      detail: `${trigger(run)} run finished successfully.`,
      status: run.status,
      at: run.ended_at_utc,
      to,
    };
  }
  return null;
}

/**
 * Every run that is news, one notification each, newest first — the order the
 * API returns them in. Exported for the unit tests.
 *
 * Deliberately **not** collapsed to one row per pipeline. A status board would
 * be smaller, but "it failed at 02:00, 03:00 and 04:00" is a different fact from
 * "it is failing", and only the log keeps it.
 */
export function notificationsFrom(runs: RecentRunOut[]): AppNotification[] {
  return runs.map(notificationFor).filter((n): n is AppNotification => n !== null);
}

/** Same notifications, in the same order? Ids are enough: a notification's
 *  content is a pure function of the run it came from, so equal ids mean equal
 *  rows. Used to keep a poll that found nothing new from causing a render. */
function same(a: AppNotification[], b: AppNotification[]): boolean {
  return a.length === b.length && a.every((n, i) => n.id === b[i]?.id);
}

export interface UseNotifications {
  /** The log, newest first, with deleted items already removed. */
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  isRead: (id: string) => boolean;
  reload: () => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  /** Delete one from this person's view. It stays deleted across polls and
   *  reloads — the run it came from has not gone anywhere. */
  dismiss: (id: string) => void;
  /** Delete everything currently listed. */
  dismissAll: () => void;
}

export function useNotifications(): UseNotifications {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  const userId = me?.user_id;
  // The full derived feed. `notifications` is this minus what was deleted —
  // both are needed, because pruning has to be judged against what the server
  // still reports, not against what this person has chosen to hide.
  const [feed, setFeed] = useState<AppNotification[]>([]);
  const [read, setRead] = useState<Set<string>>(() => new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  // Read inside `load` through refs so its dependency list can stay empty —
  // see the module docstring for what happens otherwise.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const waiting = useWaitingItems();
  // The pipeline log's latest answer; `ok` false when its last poll failed.
  const [runs, setRuns] = useState<{ items: AppNotification[]; ok: boolean } | null>(null);

  // Reload both id sets when the signed-in user changes.
  useEffect(() => {
    setRead(loadIds("read", userId));
    setDismissed(loadIds("dismissed", userId));
  }, [userId]);

  const loadRuns = useCallback(async () => {
    try {
      const token = await getTokenRef.current();
      const recent = await listRecentRuns(token, { background: true });
      const next = notificationsFrom(recent.items);
      // Only re-render when something actually changed. A fresh array every
      // minute would re-render every consumer for nothing — and, with a loose
      // dependency, would be the difference between an extra render and a
      // runaway loop.
      setRuns((current) =>
        current && current.ok && same(current.items, next) ? current : { items: next, ok: true },
      );
    } catch {
      // A failed poll keeps the last state and retries next tick.
      setRuns((current) => ({ items: current?.items ?? [], ok: false }));
    }
  }, []);

  // Starts once the profile has loaded; `userId` is a string, so this does not
  // re-run on every render.
  useEffect(() => {
    if (!userId) return undefined;
    void loadRuns();
    const timer = window.setInterval(() => void loadRuns(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadRuns, userId]);

  // All or nothing: the feed — and the pruning judged against it — moves only
  // when both sources answered on their latest try. Half a feed would be
  // pruned against, which would forget the read/deleted marks of the half that
  // was missing.
  const waitingItems = waiting.items;
  const waitingFailed = waiting.failed;
  useEffect(() => {
    if (!runs || !runs.ok || waitingItems === null || waitingFailed) return;
    const next = [...waitingNotifications(waitingItems), ...runs.items];
    setFeed((current) => (same(current, next) ? current : next));

    // Housekeeping: drop ids that no longer exist so neither set can grow
    // without bound as runs age out of the seven-day window. Judged against
    // the whole feed — pruning against the visible list would delete every
    // dismissal the moment it took effect, and the rows would come back.
    const live = new Set(next.map((n) => n.id));
    const prune = (kind: IdSet) => (prev: Set<string>) => {
      const pruned = new Set([...prev].filter((id) => live.has(id)));
      if (pruned.size === prev.size) return prev;
      saveIds(kind, userIdRef.current, pruned);
      return pruned;
    };
    setRead(prune("read"));
    setDismissed(prune("dismissed"));
  }, [runs, waitingItems, waitingFailed]);

  const loading = runs === null || (waitingItems === null && !waitingFailed);
  const refreshWaiting = waiting.refresh;
  const load = useCallback(() => {
    void loadRuns();
    refreshWaiting();
  }, [loadRuns, refreshWaiting]);

  const notifications = useMemo(() => feed.filter((n) => !dismissed.has(n.id)), [feed, dismissed]);

  /** Add ids to one of the sets, persisting only when something changed. */
  const add = useCallback(
    (kind: IdSet, setter: typeof setRead, ids: string[]) => {
      setter((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        if (next.size === prev.size) return prev;
        saveIds(kind, userId, next);
        return next;
      });
    },
    [userId],
  );

  const markRead = useCallback((id: string) => add("read", setRead, [id]), [add]);

  const markAllRead = useCallback(
    () =>
      add(
        "read",
        setRead,
        notifications.map((n) => n.id),
      ),
    [add, notifications],
  );

  const dismiss = useCallback((id: string) => add("dismissed", setDismissed, [id]), [add]);

  const dismissAll = useCallback(
    () =>
      add(
        "dismissed",
        setDismissed,
        notifications.map((n) => n.id),
      ),
    [add, notifications],
  );

  const unreadCount = useMemo(
    () => notifications.reduce((n, item) => n + (read.has(item.id) ? 0 : 1), 0),
    [notifications, read],
  );

  const isRead = useCallback((id: string) => read.has(id), [read]);

  return {
    notifications,
    unreadCount,
    loading,
    isRead,
    reload: load,
    markRead,
    markAllRead,
    dismiss,
    dismissAll,
  };
}
